import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Columns3, FileSpreadsheet, FileText } from 'lucide-react';

const exportRows = (rows, cols) => rows.map((r) => Object.fromEntries(
  cols.map((c) => [c.label, c.exportValue ? c.exportValue(r) : (Array.isArray(r[c.key]) ? r[c.key].join(', ') : r[c.key] ?? '')]),
));

const ColumnOption = ({ c, visible, toggle, testId }) => (
  <label className="flex items-center gap-2 px-1 py-1 text-sm hover:bg-gray-50 rounded cursor-pointer">
    <input type="checkbox" checked={visible.includes(c.key)} onChange={() => toggle(c.key)} data-testid={`${testId}-col-${c.key}`} />
    {c.label}
  </label>
);

const SectionGroup = ({ section, cols, visible, setVisible, toggle, testId }) => {
  const keys = cols.map((c) => c.key);
  const all = keys.every((k) => visible.includes(k));
  const flip = () => setVisible(all ? visible.filter((k) => !keys.includes(k)) : [...new Set([...visible, ...keys])]);
  return (
    <div className="mb-2" data-testid={`${testId}-section-${section.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between px-1 py-1 sticky top-0 bg-gray-50 rounded">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{section}</span>
        <button onClick={flip} className="text-[11px] text-blue-600">{all ? 'Clear' : 'All'}</button>
      </div>
      {cols.map((c) => <ColumnOption key={c.key} c={c} visible={visible} toggle={toggle} testId={testId} />)}
    </div>
  );
};

const ColumnPicker = ({ columns, visible, setVisible, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const toggle = (k) => setVisible(visible.includes(k) ? visible.filter((x) => x !== k) : [...visible, k]);
  const sections = columns.some((c) => c.section)
    ? [...new Set(columns.map((c) => c.section))].map((sec) => ({ sec, cols: columns.filter((c) => c.section === sec) }))
    : null;
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`${testId}-columns-btn`}
        className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
        <Columns3 size={14} className="mr-1.5" /> Columns ({visible.length}/{columns.length})
      </button>
      {open && (
        <div className={`absolute right-0 z-20 mt-1 ${sections ? 'w-80 max-h-[28rem]' : 'w-64 max-h-80'} overflow-auto bg-white border rounded-lg shadow-lg p-2`} data-testid={`${testId}-columns-menu`}>
          <div className="flex justify-between px-1 pb-2 border-b mb-1 text-xs">
            <button className="text-blue-600" onClick={() => setVisible(columns.map((c) => c.key))} data-testid={`${testId}-columns-all`}>Select all</button>
            <button className="text-gray-500" onClick={() => setVisible([])} data-testid={`${testId}-columns-none`}>Clear</button>
          </div>
          {sections
            ? sections.map(({ sec, cols }) => <SectionGroup key={sec} section={sec} cols={cols} visible={visible} setVisible={setVisible} toggle={toggle} testId={testId} />)
            : columns.map((c) => <ColumnOption key={c.key} c={c} visible={visible} toggle={toggle} testId={testId} />)}
        </div>
      )}
    </div>
  );
};

export const CustomizableReportTable = ({ columns, rows, defaultVisible, storageKey, fileName, testId = 'report', rowKey, toolbar, onVisibleChange }) => {
  const [visible, setVisibleState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (Array.isArray(saved) && saved.length) return saved.filter((k) => columns.some((c) => c.key === k));
    } catch { /* ignore */ }
    return defaultVisible || columns.map((c) => c.key);
  });
  const setVisible = (v) => setVisibleState(columns.filter((c) => v.includes(c.key)).map((c) => c.key));
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visible));
    if (onVisibleChange) onVisibleChange(visible);
  }, [visible, storageKey]); // eslint-disable-line
  const cols = useMemo(() => columns.filter((c) => visible.includes(c.key)), [columns, visible]);

  const doExport = (type) => {
    const ws = XLSX.utils.json_to_sheet(exportRows(rows, cols));
    if (type === 'csv') {
      saveAs(new Blob([XLSX.utils.sheet_to_csv(ws)], { type: 'text/csv;charset=utf-8' }), `${fileName}.csv`);
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
  };

  return (
    <div className="bg-white border rounded-lg" data-testid={`${testId}-table-wrap`}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b">
        <div className="flex-1 flex flex-wrap gap-2 items-center">{toolbar}</div>
        <span className="text-xs text-gray-500" data-testid={`${testId}-row-count`}>{rows.length} row{rows.length !== 1 ? 's' : ''}</span>
        <ColumnPicker columns={columns} visible={visible} setVisible={setVisible} testId={testId} />
        <button onClick={() => doExport('xlsx')} disabled={!rows.length || !cols.length} data-testid={`${testId}-export-xlsx`}
          className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50">
          <FileSpreadsheet size={14} className="mr-1.5" /> Excel
        </button>
        <button onClick={() => doExport('csv')} disabled={!rows.length || !cols.length} data-testid={`${testId}-export-csv`}
          className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          <FileText size={14} className="mr-1.5" /> CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testId}-table`}>
          <thead>
            <tr className="bg-gray-50 border-b text-left text-gray-600">
              {cols.map((c) => <th key={c.key} className={`px-3 py-2.5 font-medium whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={Math.max(cols.length, 1)} className="px-4 py-10 text-center text-gray-400" data-testid={`${testId}-empty`}>No rows to display</td></tr>
            ) : rows.map((r, i) => (
              <tr key={rowKey ? rowKey(r, i) : i} className="border-b hover:bg-blue-50/30" data-testid={`${testId}-row-${i}`}>
                {cols.map((c) => (
                  <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                    {c.render ? c.render(r) : (Array.isArray(r[c.key]) ? r[c.key].join(', ') : (r[c.key] === '' || r[c.key] == null ? '—' : r[c.key]))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
